import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import * as z from 'zod';
import { api } from '@/api';
import { Button } from '@/ui/components/button';
import { LoadingState } from '@/ui/components/loading-state';
import {
  SHAREPOINT_CODE_VERIFIER_SESSION_KEY,
  SHAREPOINT_OAUTH_MESSAGE,
  SHAREPOINT_POST_LOGIN_RETURN_URL,
  SHAREPOINT_POST_LOGOUT_RETURN_URL,
  SHAREPOINT_STATE_PARAM,
} from '@/features/sharepoint/constants';
import {
  PROCORE_CODE_VERIFIER_SESSION_KEY,
  PROCORE_OAUTH_MESSAGE,
  PROCORE_POST_LOGIN_RETURN_URL,
  PROCORE_STATE_PARAM,
} from '@/features/procore/constants';
import {
  completeOauthPopup,
  getOauthStorage,
  removeOauthStorage,
} from '@/features/sharepoint/utils';

export const Route = createFileRoute('/_auth/auth/callback')({
  component: RouteComponent,
  validateSearch: z.object({
    code: z.string().optional(),
    error: z.string().optional(),
    state: z.string().optional(),
  }),
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);

  const clearReturnUrls = () => {
    removeOauthStorage(SHAREPOINT_POST_LOGOUT_RETURN_URL);
    removeOauthStorage(SHAREPOINT_POST_LOGIN_RETURN_URL);
    removeOauthStorage(PROCORE_POST_LOGIN_RETURN_URL);
  };

  const isReturnToValid = (returnTo: string) => {
    return returnTo.startsWith(window.location.origin);
  };

  const getReturnToUrl = () => {
    const returnTo =
      getOauthStorage(SHAREPOINT_POST_LOGOUT_RETURN_URL) ??
      getOauthStorage(SHAREPOINT_POST_LOGIN_RETURN_URL) ??
      getOauthStorage(PROCORE_POST_LOGIN_RETURN_URL);

    if (returnTo && isReturnToValid(returnTo)) {
      return returnTo;
    } else {
      return '/';
    }
  };

  const handleRedirectOnError = () => {
    if (completeOauthPopup(PROCORE_OAUTH_MESSAGE)) return;
    if (completeOauthPopup(SHAREPOINT_OAUTH_MESSAGE)) return;
    const returnTo = getReturnToUrl();
    clearReturnUrls();
    window.location.replace(returnTo);
  };

  const completeLogin = (returnUrlKey: string, keysToClear: string[], messageType: string) => {
    const returnToItem = getOauthStorage(returnUrlKey);
    const returnToUrl = returnToItem && isReturnToValid(returnToItem) ? returnToItem : '/';
    keysToClear.forEach((key) => removeOauthStorage(key));
    clearReturnUrls();
    if (completeOauthPopup(messageType)) return;
    window.location.replace(returnToUrl);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const code = searchParams.code;
    const err = searchParams.error;
    const state = searchParams.state;
    const sharepointVerifier = getOauthStorage(SHAREPOINT_CODE_VERIFIER_SESSION_KEY) || '';
    const procoreVerifier = getOauthStorage(PROCORE_CODE_VERIFIER_SESSION_KEY) || '';
    const postLogoutReturnTo = getOauthStorage(SHAREPOINT_POST_LOGOUT_RETURN_URL);
    const isProcore = state === getOauthStorage(PROCORE_STATE_PARAM);
    const isSharepoint = state === getOauthStorage(SHAREPOINT_STATE_PARAM);

    if (err) {
      setError(err);
      return;
    }

    if (code && !sharepointVerifier && !procoreVerifier) {
      setError(t(common.somethingWentWrong));
      return;
    }

    if (!code && !postLogoutReturnTo && !sharepointVerifier && !procoreVerifier) {
      setError(t(common.somethingWentWrong));
      return;
    }

    if (code && isProcore && procoreVerifier) {
      api
        .post('v1/integrations/procore/token', { code, codeVerifier: procoreVerifier })
        .then(() =>
          completeLogin(
            PROCORE_POST_LOGIN_RETURN_URL,
            [PROCORE_CODE_VERIFIER_SESSION_KEY, PROCORE_STATE_PARAM],
            PROCORE_OAUTH_MESSAGE,
          ),
        )
        .catch(() => setError(t(common.somethingWentWrong)));
      return;
    }

    if (code && isSharepoint && sharepointVerifier) {
      api
        .post('v1/integrations/sharepoint/token', { code, codeVerifier: sharepointVerifier })
        .then(() =>
          completeLogin(
            SHAREPOINT_POST_LOGIN_RETURN_URL,
            [SHAREPOINT_CODE_VERIFIER_SESSION_KEY, SHAREPOINT_STATE_PARAM],
            SHAREPOINT_OAUTH_MESSAGE,
          ),
        )
        .catch(() => setError(t(common.somethingWentWrong)));
      return;
    }

    if (code) {
      setError(t(common.somethingWentWrong));
      return;
    }

    if (postLogoutReturnTo) {
      clearReturnUrls();
      window.location.replace(isReturnToValid(postLogoutReturnTo) ? postLogoutReturnTo : '/');
      return;
    }

    navigate({ to: '/', replace: true });
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      {error && (
        <div className="flex flex-col items-center gap-5">
          <div className="rounded-xl bg-red-100 p-4 text-red-900 shadow">{error}</div>
          <Button onClick={handleRedirectOnError}>
            {t({ en: 'Return to Volve', nb: 'Tilbake til Volve' })}
          </Button>
        </div>
      )}

      {!error && <LoadingState context="auth" />}
    </div>
  );
}
